import React, { useState, useEffect } from 'react';
import CarouselMedia from './CarouselMedia';

interface MediaItem {
  url: string;
  type: 'image' | 'video' | 'youtube';
}

// Función para obtener la clave específica del cliente
const getConfigKey = (client: string) => `carousel_config_${client}`;

// Valores por defecto específicos para cada cliente
const defaultConfigs = {
  Divri: {
    mediaList: [
      { url: "https://ioipcorestorageaccount.blob.core.windows.net/ioip-core-static-files/clients/divri/DIGITURNO thumbnail_Imagen_pq.jpg", type: 'image' as 'image' },
      { url: "https://ioipcorestorageaccount.blob.core.windows.net/ioip-core-static-files/clients/divri/prueba-digiturno.mp4", type: 'video' as 'video' }
    ],
    interval: 5000,
    containerBg: '#e6f0ff',
    tableBg: '#3a5fc8',
    tableText: '#ffffff',
    containerBorder: '#3a5fc8',
    imageBorder: '#3a5fc8',
    enableAudio: false,
  },
  MDN: {
    mediaList: [
      { url: "https://ioipcorestorageaccount.blob.core.windows.net/ioip-core-static-files/clients/mdn/VID-20230712-WA0000.mp4", type: 'video' as 'video' }
    ],
    interval: 5000,
    containerBg: '#e6f0ff',
    tableBg: '#3a5fc8',
    tableText: '#ffffff',
    containerBorder: '#3a5fc8',
    imageBorder: '#3a5fc8',
    enableAudio: true,
  }
};

const AdminCarouselConfig: React.FC = () => {
  // Detectar automáticamente el cliente basado en window.domain
  const getCurrentClient = (): 'Divri' | 'MDN' => {
    // Usar window.domain si está disponible, sino usar hostname como fallback
    if (typeof window !== 'undefined' && (window as any).domain) {
      return (window as any).domain as 'Divri' | 'MDN';
    }
    
    // Fallback: detectar por hostname
    const domain = window.location.hostname.toLowerCase();
    if (domain.includes('mdn') || domain.includes('localhost')) {
      return 'MDN';
    }
    return 'Divri';
  };

  const currentClient = getCurrentClient();
  const [selectedClient, setSelectedClient] = useState<'Divri' | 'MDN'>(currentClient);
  const [mediaList, setMediaList] = useState<MediaItem[]>(defaultConfigs[currentClient].mediaList);
  const [interval, setIntervalValue] = useState<number>(defaultConfigs[currentClient].interval);
  const [containerBg, setContainerBg] = useState<string>(defaultConfigs[currentClient].containerBg);
  const [tableBg, setTableBg] = useState<string>(defaultConfigs[currentClient].tableBg);
  const [tableText, setTableText] = useState<string>(defaultConfigs[currentClient].tableText);
  const [containerBorder, setContainerBorder] = useState<string>(defaultConfigs[currentClient].containerBorder);
  const [imageBorder, setImageBorder] = useState<string>(defaultConfigs[currentClient].imageBorder);
  const [enableAudio, setEnableAudio] = useState<boolean>(defaultConfigs[currentClient].enableAudio);
  const [preview, setPreview] = useState(false);
  const [previewHeight, setPreviewHeight] = useState<number>(500);

  // Función para obtener la configuración por defecto del cliente seleccionado
  const getDefaultConfig = (client: 'Divri' | 'MDN') => {
    return defaultConfigs[client];
  };

  // Actualizar configuración cuando cambie el cliente seleccionado
  useEffect(() => {
    const config = getDefaultConfig(selectedClient);
    setMediaList(config.mediaList);
    setIntervalValue(config.interval);
    setContainerBg(config.containerBg);
    setTableBg(config.tableBg);
    setTableText(config.tableText);
    setContainerBorder(config.containerBorder);
    setImageBorder(config.imageBorder);
    setEnableAudio(config.enableAudio);
  }, [selectedClient]);

  // Cargar configuración guardada
  useEffect(() => {
    const saved = localStorage.getItem(getConfigKey(selectedClient));
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.mediaList) && parsed.mediaList.length > 0) {
          setMediaList(parsed.mediaList);
        }
        if (typeof parsed.interval === 'number' && parsed.interval > 0) {
          setIntervalValue(parsed.interval);
        }
        if (typeof parsed.containerBg === 'string') {
          setContainerBg(parsed.containerBg);
        }
        if (typeof parsed.tableBg === 'string') {
          setTableBg(parsed.tableBg);
        }
        if (typeof parsed.tableText === 'string') {
          setTableText(parsed.tableText);
        }
        if (typeof parsed.containerBorder === 'string') {
          setContainerBorder(parsed.containerBorder);
        }
        if (typeof parsed.imageBorder === 'string') {
          setImageBorder(parsed.imageBorder);
        }
        if (typeof parsed.enableAudio === 'boolean') {
          setEnableAudio(parsed.enableAudio);
        }
      } catch {}
    }
  }, [selectedClient]);

  // Guardar configuración
  const saveConfig = () => {
    localStorage.setItem(getConfigKey(selectedClient), JSON.stringify({ mediaList, interval, containerBg, tableBg, tableText, containerBorder, imageBorder, enableAudio }));
    alert('¡Configuración guardada!');
  };

  // Restablecer a valores por defecto del cliente seleccionado
  const resetToDefault = () => {
    if (window.confirm(`¿Estás seguro de que quieres restablecer la configuración a los valores por defecto de ${selectedClient}? Esta acción no se puede deshacer.`)) {
      localStorage.removeItem(getConfigKey(selectedClient));
      const config = getDefaultConfig(selectedClient);
      setMediaList(config.mediaList);
      setIntervalValue(config.interval);
      setContainerBg(config.containerBg);
      setTableBg(config.tableBg);
      setTableText(config.tableText);
      setContainerBorder(config.containerBorder);
      setImageBorder(config.imageBorder);
      setEnableAudio(config.enableAudio);
      alert(`¡Configuración restablecida a los valores por defecto de ${selectedClient}!`);
    }
  };

  // Manejar cambios en la lista
  const updateMedia = (idx: number, field: keyof MediaItem, value: string) => {
    setMediaList(list => list.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };
  const addMedia = () => setMediaList(list => [...list, { url: '', type: 'image' }]);
  const removeMedia = (idx: number) => setMediaList(list => list.filter((_, i) => i !== idx));

  // Validación mejorada
  const isValidUrl = (url: string, type: 'image' | 'video' | 'youtube') => {
    if (type === 'youtube') {
      // Para YouTube aceptamos URLs completas, cortas o solo el ID
      return url.includes('youtube.com/watch') || 
             url.includes('youtu.be/') || 
             (url.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(url));
    }
    return /^https?:\/\//.test(url);
  };
  
  const isValid = mediaList.length > 0 && 
                  mediaList.every(m => isValidUrl(m.url, m.type) && (m.type === 'image' || m.type === 'video' || m.type === 'youtube')) && 
                  interval > 0;

  // Renderizar preview según el cliente seleccionado
  const renderPreview = () => {
    if (!preview || !isValid) return null;

    if (selectedClient === 'Divri') {
      return (
        <div style={{ 
          marginTop: 32, 
          height: previewHeight, 
          border: '1px solid #ccc', 
          borderRadius: 6, 
          overflow: 'hidden',
          minHeight: '300px',
          maxHeight: '800px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: containerBg,
            border: `2px solid ${containerBorder}`,
            borderRadius: 8,
            padding: 10,
            width: '100%',
            boxSizing: 'border-box',
            height: '100%',
            position: 'relative'
          }}>
            <div style={{
              width: '98%',
              marginBottom: 10,
              border: `2px solid ${imageBorder}`,
              borderRadius: 6,
              overflow: 'hidden',
              height: '70%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CarouselMedia mediaList={mediaList} interval={interval} enableAudio={enableAudio} />
            </div>
            <div style={{ width: '98%', flex: '0 0 30%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              <div style={{
                display: 'flex',
                background: tableBg,
                color: tableText,
                fontWeight: 'bold',
                fontSize: 16,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4
              }}>
                <div style={{ flex: 2, padding: 6, borderRight: '2px solid white', textAlign: 'center' }}>NOMBRE</div>
                <div style={{ flex: 1, padding: 6, borderRight: '2px solid white', textAlign: 'center' }}>TURNO</div>
                <div style={{ flex: 1, padding: 6, textAlign: 'center' }}>MÓDULO</div>
              </div>
              <div style={{ flex: 1, background: '#f4f8ff', borderBottom: '1px solid #b3c6e6', fontSize: 14, padding: 8 }}>
                Vista previa de la tabla de turnos
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Preview para MDN
      return (
        <div style={{ 
          marginTop: 32, 
          height: previewHeight, 
          border: '1px solid #ccc', 
          borderRadius: 6, 
          overflow: 'hidden', 
          background: '#fff',
          minHeight: '300px',
          maxHeight: '800px'
        }}>
          <div style={{ height: '80px', background: 'white', borderBottom: '1px solid #ccc', display: 'flex', alignItems: 'center', padding: '10px' }}>
            <div style={{ height: '60px', width: '120px', background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
              Logo MDN
            </div>
          </div>
          <div style={{ display: 'flex', height: 'calc(100% - 200px)' }}>
            <div style={{ 
              width: '68%', 
              height: '100%', 
              border: '1px solid #ccc', 
              margin: '10px',
              borderRadius: 4,
              overflow: 'hidden'
            }}>
              <CarouselMedia mediaList={mediaList} interval={interval} enableAudio={enableAudio} />
            </div>
            <div style={{ 
              width: '30%', 
              height: '100%', 
              background: '#f9f9f9', 
              margin: '10px',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12
            }}>
              Lista de turnos
            </div>
          </div>
          <div style={{ height: '120px', background: '#f5f5f5', borderTop: '1px solid #ccc', display: 'flex', alignItems: 'center', padding: '0 20px', fontSize: 12 }}>
            Footer con información y marquesina
          </div>
        </div>
      );
    }
  };

  return (
    <div style={{ 
      maxWidth: '95vw', 
      width: '100%', 
      margin: '20px auto', 
      padding: '20px', 
      border: '1px solid #3a5fc8', 
      borderRadius: 8, 
      background: '#f8faff',
      boxSizing: 'border-box',
      maxHeight: '90vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'thin',
      scrollbarColor: '#3a5fc8 #f8faff'
    }}>
      <h2>Administración Carrusel Multimedia - {selectedClient}</h2>
      
      <div style={{ marginBottom: 20 }}>
        <div style={{ 
          padding: 12, 
          background: selectedClient === 'MDN' ? '#e3f2fd' : '#e8f5e8', 
          border: `1px solid ${selectedClient === 'MDN' ? '#2196f3' : '#4caf50'}`, 
          borderRadius: 6,
          marginBottom: 12
        }}>
          <strong>Cliente detectado: {selectedClient}</strong>
          <div style={{ marginTop: 4, fontSize: 11, color: '#666', fontStyle: 'italic' }}>
            Detectado desde: {typeof window !== 'undefined' && (window as any).domain ? 'window.domain' : 'hostname'}
          </div>
          <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
            {selectedClient === 'Divri' ? 
              'Configuración para Divri: Carrusel con tabla de turnos integrada' :
              'Configuración para MDN: Carrusel en área multimedia manteniendo diseño original'
            }
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
            {selectedClient === 'Divri' ? 
              'Valores por defecto: Imagen DIGITURNO + Video prueba-digiturno, 5s intervalo' :
              'Valores por defecto: Video VID-20230712-WA0000.mp4, 5s intervalo. El carrusel se activa solo con múltiples videos.'
            }
          </div>
        </div>
        {selectedClient === 'MDN' && (
          <div style={{ marginTop: 8, padding: 8, background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, fontSize: 12 }}>
            <strong>ℹ️ Nota para MDN:</strong> Por defecto, MDN usa un video fijo con controles habilitados para mejor compatibilidad. Para activar el carrusel, agrega múltiples videos o imágenes en la sección "Recursos Multimedia" y guarda la configuración.
          </div>
        )}
      </div>

      <div>
        <label>Tiempo de transición (ms): </label>
        <input type="number" min={1000} value={interval} onChange={e => setIntervalValue(Number(e.target.value))} style={{ width: 100 }} />
      </div>

      {selectedClient === 'Divri' && (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <label>Color fondo contenedor: </label>
          <input type="color" value={containerBg} onChange={e => setContainerBg(e.target.value)} style={{ marginRight: 16 }} />
          <label>Color borde contenedor: </label>
          <input type="color" value={containerBorder} onChange={e => setContainerBorder(e.target.value)} style={{ marginRight: 16 }} />
          <label>Color fondo tabla: </label>
          <input type="color" value={tableBg} onChange={e => setTableBg(e.target.value)} style={{ marginRight: 16 }} />
          <label>Color texto tabla: </label>
          <input type="color" value={tableText} onChange={e => setTableText(e.target.value)} style={{ marginRight: 16 }} />
          <label>Color borde imagen: </label>
          <input type="color" value={imageBorder} onChange={e => setImageBorder(e.target.value)} />
        </div>
      )}

      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <label>
          <input 
            type="checkbox" 
            checked={enableAudio} 
            onChange={e => setEnableAudio(e.target.checked)} 
            style={{ marginRight: 8 }}
          />
          Habilitar audio en videos
        </label>
        {enableAudio && (
          <div style={{ marginTop: 8, padding: 8, background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 4, fontSize: 14 }}>
            <strong>⚠️ Nota importante:</strong> Para que el audio funcione, el usuario debe interactuar primero con la página (hacer clic, tocar, etc.). 
            Esto es una política de seguridad del navegador.
          </div>
        )}
      </div>

      <h3>Recursos Multimedia</h3>
      
      {/* Información sobre URLs de YouTube */}
      <div style={{ marginBottom: 16, padding: 12, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }}>
        <strong>📹 Información sobre URLs:</strong>
        <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
          <li><strong>Imágenes:</strong> URL completa (https://ejemplo.com/imagen.jpg)</li>
          <li><strong>Videos:</strong> URL completa (https://ejemplo.com/video.mp4)</li>
          <li><strong>YouTube:</strong> Puedes usar cualquiera de estos formatos:
            <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
              <li>URL completa: https://www.youtube.com/watch?v=dQw4w9WgXcQ</li>
              <li>URL corta: https://youtu.be/dQw4w9WgXcQ (con o sin parámetros adicionales)</li>
              <li>Solo ID: dQw4w9WgXcQ</li>
            </ul>
            <div style={{ marginTop: 4, fontSize: 11, color: '#666', fontStyle: 'italic' }}>
              Nota: Las URLs con parámetros adicionales (como ?list=...) se procesan automáticamente
            </div>
          </li>
        </ul>
      </div>
      
      {mediaList.map((media, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <input
            type="text"
            placeholder={media.type === 'youtube' ? 'URL de YouTube o ID del video' : 'URL de imagen o video'}
            value={media.url}
            onChange={e => updateMedia(idx, 'url', e.target.value)}
            style={{ flex: 2, marginRight: 8 }}
          />
          <select value={media.type} onChange={e => updateMedia(idx, 'type', e.target.value)} style={{ flex: 1, marginRight: 8 }}>
            <option value="image">Imagen</option>
            <option value="video">Video</option>
            <option value="youtube">YouTube</option>
          </select>
          <button onClick={() => removeMedia(idx)} disabled={mediaList.length === 1}>Eliminar</button>
        </div>
      ))}
      <button onClick={addMedia}>Agregar recurso</button>
      
      <br /><br />
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={saveConfig} disabled={!isValid}>Guardar configuración</button>
        <button onClick={resetToDefault} style={{ background: '#dc3545', color: 'white', border: '1px solid #dc3545' }}>Restablecer a valores por defecto</button>
        <button onClick={() => setPreview(p => !p)}>{preview ? 'Ocultar' : 'Previsualizar'}</button>
        
        {preview && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <label style={{ fontSize: 12 }}>Altura preview:</label>
            <input 
              type="range" 
              min="300" 
              max="800" 
              step="50" 
              value={previewHeight} 
              onChange={e => setPreviewHeight(Number(e.target.value))}
              style={{ width: 100 }}
            />
            <span style={{ fontSize: 12, minWidth: 40 }}>{previewHeight}px</span>
          </div>
        )}
      </div>
      
      {!isValid && <div style={{ color: 'red', marginTop: 8 }}>Verifica que todas las URLs sean válidas y el tiempo sea mayor a 0.</div>}
      
      {renderPreview()}
      
      {/* Indicador de scroll */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: 16, 
        padding: 8, 
        fontSize: 12, 
        color: '#666',
        borderTop: '1px solid #e0e0e0'
      }}>
        💡 Usa la rueda del mouse o las teclas ↑↓ para navegar por toda la configuración
      </div>
    </div>
  );
};

export default AdminCarouselConfig; 